import React, { useState, FormEvent, useEffect, useRef } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { setJustLoggedIn } from '../hooks/useWelcomeAnimation';
import useSounds from '../hooks/useSounds';
import { GoogleSignInButton, AuthDivider } from './auth';
import { getUserProfile, isInvestor, initializeUserSubscription } from '../services/userApi';
import Logo from './Logo';
import { logAuthError } from '../services/errorLogger';

interface FirebaseError extends Error {
  code?: string;
}

const Login: React.FC = () => {
  const [isLogin, setIsLogin] = useState<boolean>(true);
  const [email, setEmail] = useState<string>('');
  const [password, setPassword] = useState<string>('');
  const [confirmPassword, setConfirmPassword] = useState<string>('');
  const [error, setError] = useState<string>('');
  const [message, setMessage] = useState<string>('');
  const [sessionExpiredMessage, setSessionExpiredMessage] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [showForgotPassword, setShowForgotPassword] = useState<boolean>(false);

  const { login, signup, resetPassword, initError, sessionExpired, clearSessionExpired } = useAuth();
  const navigate = useNavigate();
  const { playSound } = useSounds();
  const [searchParams] = useSearchParams();
  const passwordInputRef = useRef<HTMLInputElement>(null);

  // Check for email parameter from quick test modal
  useEffect(() => {
    const emailParam = searchParams.get('email');
    if (emailParam) {
      setEmail(emailParam);
      setIsLogin(false); // Switch to signup mode
      // Focus on password field after a short delay to ensure render
      setTimeout(() => {
        passwordInputRef.current?.focus();
      }, 100);
    }
  }, [searchParams]);

  // Show session expired message
  useEffect(() => {
    if (sessionExpired) {
      setSessionExpiredMessage(true);
      // Clear the sessionExpired flag after showing the message
      clearSessionExpired();
    }
  }, [sessionExpired, clearSessionExpired]);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    setError('');
    setMessage('');
    setSessionExpiredMessage(false);

    if (!isLogin && password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }

    setLoading(true);

    try {
      let userCredential;
      if (isLogin) {
        userCredential = await login(email, password);
      } else {
        userCredential = await signup(email, password);
      }
      // Track Google Ads conversion on login and signup
      if (typeof window.gtag === 'function') {
        window.gtag('event', 'conversion', {
          'send_to': 'AW-1007677041/qTLNCM7gmeYcEPHcv-AD',
          'value': 1.0,
          'currency': 'UAH'
        });
      }
      // Play login sound
      playSound('login');
      // Set flag to show welcome animation
      setJustLoggedIn();

      // Initialize subscription immediately after sign-in/sign-up
      // This triggers the Telegram notification for new users and ensures user appears in admin panel
      try {
        await initializeUserSubscription(
          userCredential.user.uid,
          userCredential.user.email || ''
        );
      } catch (initError) {
        console.error('Failed to initialize subscription:', initError);
        // Continue with navigation even if subscription init fails
      }

      // Check if user is an investor - redirect to investor page instead of app
      try {
        const profile = await getUserProfile(userCredential.user.uid);
        if (isInvestor(profile.type)) {
          navigate('/investor');
          return;
        }
      } catch {
        // If profile fetch fails, just proceed to app
      }

      // Navigate to app for regular users
      navigate('/app');
    } catch (err) {
      const firebaseError = err as FirebaseError;
      console.error(err);
      if (firebaseError.code === 'auth/network-request-failed') {
        setError('Network error. Please check your internet connection and try again.');
        // Log as critical error with full details
        logAuthError(
          firebaseError.code,
          firebaseError.message || 'Network request failed',
          email,
          {
            action: isLogin ? 'login' : 'signup',
            browserInfo: {
              language: navigator.language,
              cookiesEnabled: navigator.cookieEnabled,
              doNotTrack: navigator.doNotTrack,
            },
          }
        );
      } else if (firebaseError.code === 'auth/user-not-found') {
        setError('No account found with this email');
      } else if (firebaseError.code === 'auth/wrong-password') {
        setError('Incorrect password');
      } else if (firebaseError.code === 'auth/invalid-credential') {
        setError('Invalid email or password. Please check your credentials and try again.');
      } else if (firebaseError.code === 'auth/email-already-in-use') {
        setError('Email already in use');
      } else if (firebaseError.code === 'auth/invalid-email') {
        setError('Invalid email address');
      } else if (firebaseError.code === 'auth/weak-password') {
        setError('Password is too weak');
      } else if (firebaseError.code === 'auth/too-many-requests') {
        setError('Too many failed attempts. Please try again later.');
      } else if (firebaseError.message) {
        setError(firebaseError.message);
      } else {
        setError('Failed to ' + (isLogin ? 'log in' : 'sign up'));
      }
    }

    setLoading(false);
  };

  const handleGoogleError = (errorMessage: string) => {
    setError(errorMessage);
    setMessage('');
    setSessionExpiredMessage(false);
  };

  const handleForgotPassword = async (e: FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    setError('');
    setMessage('');

    if (!email) {
      setError('Please enter your email address');
      return;
    }

    setLoading(true);

    try {
      await resetPassword(email);
      setMessage('Password reset email sent! Check your inbox.');
      setShowForgotPassword(false);
    } catch (err) {
      const firebaseError = err as FirebaseError;
      if (firebaseError.code === 'auth/user-not-found') {
        setError('No account found with this email');
      } else {
        setError('Failed to send reset email');
      }
    }

    setLoading(false);
  };

  // Show error if Firebase failed to initialize
  if (initError) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-blue-50 to-white flex items-center justify-center px-4">
        <div className="max-w-md w-full text-center">
          <div className="bg-red-50 border border-red-200 rounded-lg p-6">
            <h2 className="text-xl font-bold text-red-700 mb-2">Connection Error</h2>
            <p className="text-red-600">Failed to connect to the server. Please try again later.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-50 to-white flex items-center justify-center px-4">
      <div className="max-w-md w-full">
        {/* Logo/Header */}
        <div className="text-center mb-8">
          <div className="flex justify-center mb-2">
            <Logo size="xl" linkTo="/" />
          </div>
          <p className="text-gray-600">
            Improve your English pronunciation with AI
          </p>
          <Link
            to="/"
            className="inline-flex items-center gap-1 mt-3 text-sm text-blue-500 hover:text-blue-700 transition-colors"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            Back to Home
          </Link>
        </div>

        {/* Login/Signup Card */}
        <div className="bg-white rounded-xl shadow-lg p-8">
          <h2 className="text-2xl font-bold text-gray-800 mb-6 text-center">
            {showForgotPassword ? 'Reset Password' : isLogin ? 'Welcome Back' : 'Create Account'}
          </h2>

          {/* Error Message */}
          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-red-700 text-sm">{error}</p>
            </div>
          )}

          {/* Success Message */}
          {message && (
            <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg">
              <p className="text-green-700 text-sm">{message}</p>
            </div>
          )}

          {/* Session Expired Message */}
          {sessionExpiredMessage && (
            <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
              <div className="flex items-center gap-2">
                <svg className="w-5 h-5 text-amber-600 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <p className="text-amber-700 text-sm">Your session has expired. Please log in again.</p>
              </div>
            </div>
          )}

          {showForgotPassword ? (
            /* Forgot Password Form */
            <form onSubmit={handleForgotPassword}>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email
                </label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 bg-white text-gray-900 placeholder-gray-400 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition"
                  placeholder="Enter your email"
                  required
                />
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full py-3 bg-blue-500 hover:bg-blue-600 text-white font-semibold rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? 'Sending...' : 'Send Reset Link'}
              </button>

              <button
                type="button"
                onClick={() => setShowForgotPassword(false)}
                className="w-full mt-3 py-2 text-gray-600 hover:text-gray-800 text-sm"
              >
                Back to Login
              </button>
            </form>
          ) : (
            /* Login/Signup Form */
            <form onSubmit={handleSubmit}>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email
                </label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 bg-white text-gray-900 placeholder-gray-400 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition"
                  placeholder="Enter your email"
                  required
                />
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Password
                </label>
                <input
                  ref={passwordInputRef}
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 bg-white text-gray-900 placeholder-gray-400 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition"
                  placeholder="Enter your password"
                  required
                />
              </div>

              {!isLogin && (
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Confirm Password
                  </label>
                  <input
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 bg-white text-gray-900 placeholder-gray-400 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition"
                    placeholder="Confirm your password"
                    required
                  />
                </div>
              )}

              {isLogin && (
                <div className="mb-4 text-right">
                  <button
                    type="button"
                    onClick={() => setShowForgotPassword(true)}
                    className="text-sm text-blue-500 hover:text-blue-700"
                  >
                    Forgot Password?
                  </button>
                </div>
              )}

              <button
                type="submit"
                disabled={loading}
                className="w-full py-3 bg-blue-500 hover:bg-blue-600 text-white font-semibold rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? 'Please wait...' : isLogin ? 'Log In' : 'Sign Up'}
              </button>

              <AuthDivider />

              <GoogleSignInButton onError={handleGoogleError} />
            </form>
          )}

          {/* Toggle Login/Signup */}
          {!showForgotPassword && (
            <div className="mt-6 text-center">
              <p className="text-gray-600">
                {isLogin ? "Don't have an account?" : 'Already have an account?'}
                <button
                  onClick={() => {
                    setIsLogin(!isLogin);
                    setError('');
                    setMessage('');
                  }}
                  className="ml-2 text-blue-500 hover:text-blue-700 font-medium"
                >
                  {isLogin ? 'Sign Up' : 'Log In'}
                </button>
              </p>
            </div>
          )}
        </div>

        {/* Footer */}
        <p className="mt-8 text-center text-sm text-gray-400">
          Powered by Firebase Authentication
        </p>
      </div>
    </div>
  );
};

export default Login;
